// ACG CRM — Microsoft Fluent Design System
// Based on Fluent 2 / Azure Portal visual language

export const C = {
  // Primary brand — Microsoft blue exact
  primary:        '#0078D4',
  primaryHover:   '#106EBE',
  primaryActive:  '#005A9E',
  primaryLight:   '#EFF6FC',
  primaryBorder:  '#C7E0F4',

  // Neutrals — Fluent neutral ramp
  white:          '#FFFFFF',
  grey2:          '#FAF9F8',
  grey4:          '#F3F2F1',
  grey6:          '#EDEBE9',
  grey8:          '#E1DFDD',
  grey20:         '#C8C6C4',
  grey30:         '#A19F9D',
  grey50:         '#605E5C',
  grey70:         '#3B3A39',
  grey90:         '#201F1E',
  black:          '#11100F',

  // Semantic colors
  success:        '#107C10',
  successLight:   '#DFF6DD',
  warning:        '#797673',
  warningLight:   '#FFF4CE',
  warningText:    '#8A8886',
  danger:         '#A4262C',
  dangerLight:    '#FDE7E9',
  dangerHover:    '#8E1E23',
  accentPurple:      '#8764B8',
  accentPurpleLight: '#F3EDF9',

  // Surface layers (Fluent elevation model)
  surfaceBase:    '#F3F2F1',  // app background
  surfaceCard:    '#FFFFFF',  // card/panel surface
  surfaceOverlay: '#FFFFFF',  // elevated panels

  // Nav — Microsoft dark nav
  nav:            '#1B1A19',
  navText:        '#F3F2F1',
  navHover:       '#2C2B2A',
  navActive:      '#0078D4',
  navBorder:      '#2C2B2A',

  // Sidebar — slightly lighter than nav
  sidebar:        '#252423',
  sidebarText:    '#C8C6C4',
  sidebarHover:   '#3B3A39',
  sidebarActive:  '#0078D4',
  sidebarActiveBg:'rgba(0,120,212,0.15)',

  // Text hierarchy
  textPrimary:    '#201F1E',
  textSecondary:  '#605E5C',
  textDisabled:   '#A19F9D',
  textOnDark:     '#FFFFFF',
  textOnPrimary:  '#FFFFFF',

  // Borders
  borderLight:    '#EDEBE9',
  borderMedium:   '#E1DFDD',
  borderDark:     '#C8C6C4',
  borderFocus:    '#0078D4',

  // Interactive
  bgActive:       '#EFF6FC',
  bgHover:        '#F3F2F1',
  bgSelected:     '#DEECF9',
};

export const F = {
  // Segoe UI Variable — Microsoft's signature font
  family: "'Segoe UI Variable', 'Segoe UI', " +
          "-apple-system, BlinkMacSystemFont, " +
          "'San Francisco', 'Helvetica Neue', " +
          "sans-serif",

  // Type scale — Fluent 2 type ramp
  xs:   11,
  sm:   12,
  base: 13,  // Fluent body default
  md:   14,
  lg:   16,
  xl:   18,
  xxl:  20,
  h1:   24,
  h2:   20,
  h3:   17,
  h4:   14,

  // Weights
  regular: 400,
  medium:  500,
  semibold: 600,
  bold:    700,
};

export const S = {
  // Spacing — 4px base grid
  xs:  4,
  sm:  8,
  md:  12,
  lg:  16,
  xl:  20,
  xxl: 24,
  xxxl: 32,
};

export const E = {
  // Fluent elevation shadows
  // https://fluent2.microsoft.design/elevation
  none:   'none',
  depth4: '0 1.6px 3.6px rgba(0,0,0,0.132), ' +
          '0 0.3px 0.9px rgba(0,0,0,0.108)',
  depth8: '0 3.2px 7.2px rgba(0,0,0,0.132), ' +
          '0 0.6px 1.8px rgba(0,0,0,0.108)',
  depth16:'0 6.4px 14.4px rgba(0,0,0,0.132), ' +
          '0 1.2px 3.6px rgba(0,0,0,0.108)',
  depth64:'0 25.6px 57.6px rgba(0,0,0,0.22), ' +
          '0 4.8px 14.4px rgba(0,0,0,0.18)',
};

export const R = {
  // Border radius — Fluent uses small radii
  none:  0,
  sm:    2,
  md:    4,
  lg:    6,
  xl:    8,
  pill:  20,
};

export const T = {
  // Transitions — Fluent motion
  fast:   'all 0.1s ease',
  base:   'all 0.15s ease',
  slow:   'all 0.2s ease',
  expand: 'all 0.2s cubic-bezier(0.1,0.9,0.2,1)',
};

// Layout dimensions
export const NAV_H       = 48;   // Fluent uses 48px nav
export const PATH_H      = 32;
export const HEADER_H    = 48;   // Command bar height
export const SIDE_W      = 220;  // Fluent nav is narrower
export const SIDE_W_COLLAPSED = 48;
export const SOFTPHONE_W = 260;
export const CARD_W      = 300;
export const QUEUE_STRIP_H = 72;

// ── Backward-compatible aliases ──────────────────────────────────────────────
// Other pages (Interventions, Sequences, Configuration, Admin, etc.) still
// reference the legacy token shape. These aliases prevent build breakage
// while those pages await their own Fluent redesign.

// Legacy color aliases on C (the new C already has 'white', 'black', 'nav')
C.text         = C.textPrimary;
C.textMid      = C.textSecondary;
C.textLight    = C.textSecondary;
C.border       = C.borderLight;
C.borderMid    = C.borderMedium;
C.bg           = C.surfaceCard;
C.bgSubtle     = C.grey2;
C.accent       = C.primary;
C.accentHover  = C.primaryHover;
C.accentLight  = C.primaryLight;
C.navSearch    = C.navHover;
C.resultBtn    = C.grey4;
C.resultBorder = C.borderDark;
C.resultText   = C.textPrimary;
C.resultHover  = C.bgHover;
C.green        = C.success;
C.amber        = C.warningText;
C.red          = C.danger;
C.blue         = C.primary;

// Legacy font aliases — old values were strings ("12px"), new are numbers.
// React inline styles accept numbers for px, so these work in style objects.
// For CSS template literals, callers must append "px" themselves.
F.xxs     = F.xs;
F.navSize = F.md;

// Legacy layout constants (were strings, now numbers — React inline styles
// accept both, but CSS template literals need "px" suffix)
// NAV_H, PATH_H, HEADER_H, SIDE_W are already exported as numbers above.

// Legacy composite style objects
const _R = `${R.md}px`;

export const BTN = {
  primary: {
    height:       32,
    padding:      "0 14px",
    background:   C.primary,
    color:        C.white,
    border:       `1px solid ${C.primary}`,
    borderRadius: _R,
    fontSize:     F.base,
    fontFamily:   F.family,
    fontWeight:   F.semibold,
    cursor:       "pointer",
    display:      "inline-flex",
    alignItems:   "center",
    gap:          "6px",
    whiteSpace:   "nowrap",
  },
  secondary: {
    height:       32,
    padding:      "0 14px",
    background:   C.surfaceCard,
    color:        C.textPrimary,
    border:       `1px solid ${C.borderMedium}`,
    borderRadius: _R,
    fontSize:     F.base,
    fontFamily:   F.family,
    fontWeight:   F.regular,
    cursor:       "pointer",
    display:      "inline-flex",
    alignItems:   "center",
    gap:          "6px",
    whiteSpace:   "nowrap",
  },
  ghost: {
    height:       32,
    padding:      "0 10px",
    background:   "transparent",
    color:        C.textPrimary,
    border:       "1px solid transparent",
    borderRadius: _R,
    fontSize:     F.base,
    fontFamily:   F.family,
    cursor:       "pointer",
    display:      "inline-flex",
    alignItems:   "center",
    gap:          "6px",
  },
};

export const INPUT = {
  height:      32,
  padding:     "0 8px",
  background:  C.surfaceCard,
  color:       C.textPrimary,
  border:      `1px solid ${C.borderMedium}`,
  borderRadius: _R,
  fontSize:    F.base,
  fontFamily:  F.family,
  outline:     "none",
  width:       "100%",
  boxSizing:   "border-box",
};

export const LABEL = {
  fontSize:      F.xs,
  fontWeight:    F.semibold,
  color:         C.textSecondary,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  fontFamily:    F.family,
};
